/**
 * The four states anything in this app reports, and the colours that say so.
 *
 * Apart from `Badge` because the same four tones are needed without a badge around them - a dot in
 * a count, a tick beside a reading, a line of coloured text - and because a shared component must
 * not reach into a feature for its types. A feature maps its own union onto this one, which makes
 * adding a state there a compile error here rather than an unstyled element.
 *
 * Every class below is a semantic token (`ok`, `warning`, `danger`), never a palette name. The
 * palette is theme.css's business: what "nominal" looks like is decided in one place.
 */

export type StatusTone = "nominal" | "warning" | "fault" | "offline";

/** Text colour alone, for a reading or a tick that is not a badge. */
export const STATUS_TEXT: Record<StatusTone, string> = {
  nominal: "text-ok",
  warning: "text-warning",
  fault: "text-danger",
  offline: "text-text-muted",
};

/** A solid fill, for a dot. */
export const STATUS_FILL: Record<StatusTone, string> = {
  nominal: "bg-ok",
  warning: "bg-warning",
  fault: "bg-danger",
  offline: "bg-neutral-300",
};

/** Text, border and a pale wash behind it. */
export const STATUS_FILLED_BADGE: Record<StatusTone, string> = {
  nominal: "text-ok border-ok bg-ok-bg",
  warning: "text-warning border-warning bg-warning-bg",
  fault: "text-danger border-danger bg-danger-bg",
  // Offline has no wash: there is no state to draw attention to, and the palette defines none.
  offline: "text-text-muted border-neutral-300",
};

/** Text and border only, for a badge sitting on a surface that already has a colour. */
export const STATUS_OUTLINE_BADGE: Record<StatusTone, string> = {
  nominal: "text-ok border-ok",
  warning: "text-warning border-warning",
  fault: "text-danger border-danger",
  offline: "text-text-muted border-neutral-300",
};
